import styled, { css } from 'styled-components';
import { parse, stringify } from 'qs';
import useSWRImmutable from 'swr/immutable';

import { Arrow } from 'shared/icons/Arrow';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { Loader } from 'shared/components/Loader';
import { setIsVariantDetailedViewOpen } from 'store/slice/buyBarSlice';
import { setMobileView } from 'store/slice/pdpStateSlice';
import { ShareButton } from 'shared/components/ShareButton';
import { Stack } from 'shared/components/Stack';
import { t } from 'localization/utils/t';
import { TAB_VIEWS, VARIANT_GRID_VIEW } from 'productTemplate/enums/buyBarMobile';
import { Update2022VariantDetail } from 'productTemplate/components/buyBar/VariantDetail/Update2022VariantDetail';
import { useLocation } from 'shared/hooks/useLocation';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { useRouter } from 'next/router';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { VariantDetailLinkHeader } from 'productTemplate/components/buyBar/VariantDetailLinkHeader';
import { WantProductButton } from 'wants/components/WantProductButton';

const TP = 'productTemplate.components.buyBar.VariantDetail.Update2022VariantDetailMobileView';

export const Update2022VariantDetailMobileView = () => {
  const router = useRouter();
  const { query, asPath } = router;
  const { variantId } = query;
  const { selectedCountryCode } = useLocation();
  const { data, isLoading } = useSWRImmutable(
    `/web-api/v1/products/${variantId as string}?countryCode=${selectedCountryCode}`,
  );

  const { name, specialType } = useStoreSelector((state) => state.pdp.productTemplate);
  const { mobileView, previousMobileView } = useStoreSelector((state) => state.pdpState);
  const { variantGridView, isVariantDetailedViewOpen, mobileBuyBarView } = useStoreSelector(
    (state) => state.buyBar,
  );
  const isSpecialVariant =
    variantGridView === VARIANT_GRID_VIEW.SPECIAL || specialType !== 'standard';
  const storeDispatch = useStoreDispatch();

  const handleBack = () => {
    const queryParams = {
      ...query,
    };
    delete queryParams.variantId;
    delete queryParams.productTemplateSlug;

    void router.replace({ pathname: asPath.split('?')?.[0], query: queryParams });

    if (mobileView === 'detail') {
      storeDispatch(
        setMobileView({
          view: previousMobileView === 'none' ? 'expanded' : previousMobileView,
          size: data.size,
        }),
      );
    }

    if (isVariantDetailedViewOpen) {
      const isUsedTab = mobileBuyBarView === TAB_VIEWS.USED;
      const newGrid = isSpecialVariant ? VARIANT_GRID_VIEW.SPECIAL : VARIANT_GRID_VIEW.DEFECTS;

      storeDispatch(
        setIsVariantDetailedViewOpen({
          isVariantDetailedViewOpen: false,
          variantGridView: isUsedTab ? VARIANT_GRID_VIEW.NONE : newGrid,
        }),
      );
    }
  };

  const { pageTitle } = usePageMetadata({
    mainTitle: document?.title,
    defaultTitle: `${name} `,
  });
  const variantUrl = (() => {
    if (!window?.location) return '';
    const { search, origin, pathname } = window.location;

    const q = parse(search);
    q.id = `${data?.id as number}`;

    return `${origin}${pathname}?${stringify(q)}`;
  })();

  return (
    <Update2022VariantDetailMobileView.Root spacing="16px" className="variantDetailMobileView">
      <>
        <Update2022VariantDetailMobileView.Header
          href="/buyer-protection-guarantee"
          before={
            <Update2022VariantDetailMobileView.BackButton onClick={handleBack}>
              <Arrow fill="currentcolor" direction="left" type="large" />
            </Update2022VariantDetailMobileView.BackButton>
          }
          title={t(`${TP}.headerTitle`, 'Buyer Protection Guaranteed')}
          after={
            <Stack direction="row" spacing="16px">
              {Boolean(variantUrl) && (
                <Update2022VariantDetailMobileView.ShareButton title={pageTitle} url={variantUrl} />
              )}
              {data && <Update2022VariantDetailMobileView.WantButton productId={data.id} />}
            </Stack>
          }
        />
        {isLoading && <Loader style={{ margin: 'auto' }} />}
        {data && <Update2022VariantDetail variantData={data} isMobile />}
      </>
    </Update2022VariantDetailMobileView.Root>
  );
};

Update2022VariantDetailMobileView.Root = styled(Stack)`
  background: ${({ theme }) => theme.colors.black};
  --local-background: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};

  z-index: ${({ theme }) => theme.zIndex.backdrop};
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
  overflow: scroll;
  ${hideScrollbars}

  padding: 16px;
`;

Update2022VariantDetailMobileView.Header = styled(VariantDetailLinkHeader)`
  flex-grow: 0;
  align-items: center;
  ${variableFontRegular({ height: '11px', size: '11' })};
`;

Update2022VariantDetailMobileView.BackButton = styled.button`
  grid-row: top / bottom;
  grid-column: start / middle-left;
  padding: 0px;
  text-align: start;

  img {
    height: auto;
    width: 16px;
  }
`;

const buttonStyles = css`
  align-items: center;
  display: flex;
  height: 20px;
  justify-content: center;
  padding: 4px;
  width: fit-content;
  border: ${({ theme }) => theme.borders.opaqueWhite};
  background: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: 32px;
  }

  ${variableFontRegular({ height: '16px', size: '12' })}
`;

Update2022VariantDetailMobileView.ShareButton = styled(ShareButton)`
  ${buttonStyles}
  padding-top: 5px;
`;

Update2022VariantDetailMobileView.WantButton = styled(WantProductButton)`
  ${buttonStyles}
  margin-left: 8px;
  padding-top: 5px;
`;
